import React, { useEffect, useState } from "react";
import { Card, SectionLabel, Button, Input } from "./ui";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { Sparkles, KeyRound, Check } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);
const PROVIDERS = [
  { id: "openai", label: "OpenAI", placeholder: "gpt-4o" },
  { id: "anthropic", label: "Anthropic", placeholder: "claude-sonnet-4-6" },
  { id: "gemini", label: "Gemini", placeholder: "gemini-2.5-pro" },
];

export default function AiProviderCard() {
  const { lang, toast } = useApp();
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("plan");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ai-settings").then(({ data }) => {
      setCfg(data);
      setMode(data.mode || "plan");
      setProvider(data.provider || "openai");
      setModel(data.model || "");
    }).catch(() => {});
  }, []);

  const providerMeta = PROVIDERS.find((p) => p.id === provider) || PROVIDERS[0];

  const usePlan = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/ai-settings", { mode: "plan" });
      setCfg(data); setMode("plan");
      toast(L(lang, "Crédits du plan activés", "Plan credits enabled"));
    } catch { toast(L(lang, "Échec de l'enregistrement", "Could not save")); }
    finally { setBusy(false); }
  };

  const saveByok = async () => {
    if (!cfg?.configured && !apiKey.trim()) {
      toast(L(lang, "Saisissez votre clé API", "Enter your API key"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/ai-settings", {
        mode: "byok", provider, model: model.trim() || undefined,
        api_key: apiKey.trim() || undefined,
      });
      setCfg(data); setMode("byok"); setApiKey("");
      toast(L(lang, "Clé enregistrée et vérifiée", "Key saved and verified"));
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || L(lang, "Clé invalide", "Invalid key");
      toast(msg);
    } finally { setBusy(false); }
  };

  const removeKey = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/ai-settings");
      setCfg(data); setMode("plan"); setApiKey("");
      toast(L(lang, "Clé supprimée", "Key removed"));
    } catch { toast(L(lang, "Échec de la suppression", "Could not remove")); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <Card className="mb-6" data-testid="ai-provider-card">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-text-secondary" />
        <SectionLabel className="!mb-0">{L(lang, "Fournisseur IA & crédits", "AI provider & credits")}</SectionLabel>
      </div>
      <p className="text-text-secondary text-sm mt-1.5 mb-4">
        {L(lang,
          "Utilisez les crédits inclus dans votre plan, ou votre propre clé API pour une utilisation illimitée facturée par votre fournisseur.",
          "Use the AI credits included in your plan, or your own API key for unlimited usage billed directly by your provider.")}
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={usePlan} disabled={busy} data-testid="ai-mode-plan"
          className={cn("text-left rounded-xl border p-3.5 transition-all",
            mode === "plan" ? "border-primary bg-primary/[0.04]" : "border-[var(--border)] hover:bg-surface-hover")}>
          <div className="flex items-center justify-between">
            <span className="font-medium text-text-primary flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" />{L(lang, "Crédits du plan", "Plan credits")}</span>
            {mode === "plan" && <Check className="w-4 h-4 text-primary" />}
          </div>
          <p className="text-xs text-text-secondary mt-1">{L(lang, "Inclus dans votre abonnement. Mesurés chaque mois.", "Included in your subscription. Metered monthly.")}</p>
        </button>

        <button onClick={() => setMode("byok")} disabled={busy} data-testid="ai-mode-byok"
          className={cn("text-left rounded-xl border p-3.5 transition-all",
            mode === "byok" ? "border-primary bg-primary/[0.04]" : "border-[var(--border)] hover:bg-surface-hover")}>
          <div className="flex items-center justify-between">
            <span className="font-medium text-text-primary flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" />{L(lang, "Ma propre clé API", "My own API key")}</span>
            {mode === "byok" && cfg.configured && <Check className="w-4 h-4 text-primary" />}
          </div>
          <p className="text-xs text-text-secondary mt-1">{L(lang, "Illimité, facturé par votre fournisseur. Pas de crédits décomptés.", "Unlimited, billed by your provider. No credits used.")}</p>
        </button>
      </div>

      {mode === "byok" && (
        <div className="mt-4 space-y-3" data-testid="ai-byok-fields">
          <div>
            <label className="text-xs text-text-secondary block mb-1.5">{L(lang, "Fournisseur", "Provider")}</label>
            <div className="inline-flex items-center gap-1 rounded-full bg-surface-hover p-0.5">
              {PROVIDERS.map((p) => (
                <button key={p.id} onClick={() => setProvider(p.id)} data-testid={`ai-provider-${p.id}`}
                  className={cn("px-3 py-1.5 rounded-full text-xs font-semibold transition-colors",
                    provider === p.id ? "bg-primary text-white" : "text-text-secondary hover:text-text-primary")}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-text-secondary block mb-1.5">{L(lang, "Modèle", "Model")}</label>
            <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder={providerMeta.placeholder}
              data-testid="ai-model-input" />
          </div>
          <div>
            <label className="text-xs text-text-secondary block mb-1.5">
              {L(lang, "Clé API", "API key")}{cfg.configured && <span className="text-text-secondary"> · {L(lang, "enregistrée", "saved")} {cfg.key_hint}</span>}
            </label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.configured ? "••••••••" : "sk-..."} data-testid="ai-key-input" />
            <p className="text-[11px] text-text-secondary mt-1.5">{L(lang, "Chiffrée au repos. Jamais affichée en clair.", "Encrypted at rest. Never shown in plain text.")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={saveByok} disabled={busy} data-testid="ai-save-btn">
              {busy ? L(lang, "Vérification…", "Verifying…") : L(lang, "Enregistrer la clé", "Save key")}
            </Button>
            {cfg.configured && (
              <Button variant="secondary" onClick={removeKey} disabled={busy} data-testid="ai-remove-btn">
                {L(lang, "Supprimer", "Remove")}
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
